import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from '../components/layout/AppShell';
import { useMode } from '../contexts/ModeContext';
import { useLang } from '../contexts/LangContext';
import { authAPI, hatimAPI, quranAPI } from '../utils/api';
import { Target, ArrowRight, RotateCcw, CheckCircle2, BookOpen as BookIcon } from 'lucide-react';
import { toast } from 'sonner';

const Hatim = () => {
  const { isKidsMode } = useMode();
  const { lang, t } = useLang();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [progress, setProgress] = useState(null);
  const [surahs, setSurahs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [jumpPage, setJumpPage] = useState('');
  const accent = isKidsMode ? 'amber' : 'teal';

  const loadAll = async () => {
    try {
      const [u, p, s] = await Promise.all([
        authAPI.getMe(),
        hatimAPI.getProgress(),
        quranAPI.getSurahList(),
      ]);
      setUser(u.data);
      setProgress(p.data);
      setSurahs(s.data?.data || []);
    } catch {
      toast.error(lang === 'tr' ? 'Yüklenemedi' : 'Failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [u, p, s] = await Promise.all([
          authAPI.getMe(),
          hatimAPI.getProgress(),
          quranAPI.getSurahList(),
        ]);
        if (cancelled) return;
        setUser(u.data);
        setProgress(p.data);
        setSurahs(s.data?.data || []);
      } catch {
        if (!cancelled) toast.error(lang === 'tr' ? 'Yüklenemedi' : 'Failed');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [lang]);

  const completeSurah = async (n) => {
    try {
      await hatimAPI.completeSurah(n);
      toast.success(lang === 'tr' ? 'Sure tamamlandı!' : 'Surah completed!');
      await loadAll();
    } catch {
      toast.error(lang === 'tr' ? 'Kaydedilemedi' : 'Save failed');
    }
  };

  const resetHatim = async () => {
    if (!window.confirm(lang === 'tr' ? 'Hatim ilerlemesi sıfırlanacak. Emin misin?' : 'Reset all hatim progress?')) return;
    await hatimAPI.reset();
    await loadAll();
    toast.success(lang === 'tr' ? 'Sıfırlandı' : 'Reset done');
  };

  if (loading) {
    return <AppShell user={user}><div className="p-8 text-center text-stone-500">{t('common.loading')}</div></AppShell>;
  }

  const completed = progress?.completed_surahs || [];
  const lastSurah = progress?.last_surah || 1;
  const lastAyah = progress?.last_ayah || 1;
  const lastSurahInfo = surahs.find((s) => s.number === lastSurah);

  return (
    <AppShell user={user} title={lang === 'tr' ? 'Hatim' : 'Hatim'}>
      <div className="max-w-6xl mx-auto p-4 sm:p-8 md:p-12 space-y-6">
        <header className="mb-6">
          <p className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-2">{lang === 'tr' ? 'Hatim' : 'Complete Quran'}</p>
          <h1 className="text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {lang === 'tr' ? 'Hatim Yolculuğun' : 'Your Hatim Journey'}
          </h1>
          <p className="text-stone-600 mt-2">
            {lang === 'tr' ? 'Kuran-ı Kerim\'i baştan sona okuma süreci. Kaldığın yerden devam et.' : 'Read the Quran from start to finish. Continue where you left off.'}
          </p>
        </header>

        {/* Quick page jump for Hatim */}
        <section className={`rounded-2xl border p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4 ${isKidsMode ? 'bg-amber-50/60 border-amber-200' : 'bg-teal-50/60 border-teal-200'}`}>
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${isKidsMode ? 'bg-amber-100 text-amber-700' : 'bg-teal-100 text-teal-800'}`}>
            <BookIcon className="w-6 h-6" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-stone-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {lang === 'tr' ? 'Sayfa Modunda Hatim' : 'Hatim in Page Mode'}
            </h3>
            <p className="text-sm text-stone-500">
              {lang === 'tr' ? 'Mushaf sayfasına atla, gerçek Kuran düzeninde oku.' : 'Jump to any Mushaf page, read in true layout.'}
            </p>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <input
              type="number"
              min={1}
              max={604}
              value={jumpPage}
              onChange={(e) => setJumpPage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return;
                const n = parseInt(jumpPage, 10);
                if (n >= 1 && n <= 604) navigate(`/oku/page/${n}`);
                else toast.error(lang === 'tr' ? '1-604 arası girin' : 'Enter 1-604');
              }}
              placeholder="1-604"
              data-testid="hatim-page-input"
              className="w-full sm:w-28 px-3 py-2 rounded-lg border border-stone-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/40"
            />
            <button
              type="button"
              onClick={() => {
                const n = parseInt(jumpPage, 10);
                if (n >= 1 && n <= 604) navigate(`/oku/page/${n}`);
                else toast.error(lang === 'tr' ? '1-604 arası girin' : 'Enter 1-604');
              }}
              data-testid="hatim-page-go"
              className={`px-4 py-2 rounded-lg font-semibold text-white text-sm whitespace-nowrap ${isKidsMode ? 'bg-amber-500 hover:bg-amber-600' : 'bg-teal-700 hover:bg-teal-800'}`}
            >
              {lang === 'tr' ? 'Aç' : 'Open'}
            </button>
          </div>
        </section>

        {/* Progress hero */}
        <section className="bg-white rounded-3xl border border-stone-200 p-8 sm:p-10">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <Target className={`w-6 h-6 ${isKidsMode ? 'text-amber-600' : 'text-teal-700'}`} />
              <span className="text-sm font-semibold text-stone-700 uppercase tracking-wider" style={{ fontFamily: 'Outfit, sans-serif' }}>{lang === 'tr' ? 'İlerleme' : 'Progress'}</span>
            </div>
            <button
              type="button"
              onClick={resetHatim}
              data-testid="hatim-reset"
              className="inline-flex items-center gap-1 text-xs text-stone-500 hover:text-stone-700"
            >
              <RotateCcw className="w-3.5 h-3.5" /> {lang === 'tr' ? 'Sıfırla' : 'Reset'}
            </button>
          </div>
          <div className="flex items-end justify-between mb-4">
            <div className="text-5xl font-bold text-stone-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {completed.length} / 114
            </div>
            <div className="text-2xl font-semibold text-stone-500" style={{ fontFamily: 'Outfit, sans-serif' }}>
              %{progress?.progress_percent || 0}
            </div>
          </div>
          <div className="w-full bg-stone-100 rounded-full h-4 overflow-hidden">
            <div
              className={`${isKidsMode ? 'bg-amber-500' : 'bg-teal-600'} h-full transition-all`}
              style={{ width: `${progress?.progress_percent || 0}%` }}
              data-testid="hatim-progressbar"
            />
          </div>
          {lastSurahInfo && (
            <div className="mt-6 pt-6 border-t border-stone-100">
              <div className="text-xs uppercase tracking-[0.2em] text-stone-500 mb-2">{lang === 'tr' ? 'Kaldığın yer' : 'Last position'}</div>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                  <div className="text-lg font-semibold text-stone-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {lastSurahInfo.name_en} — {lang === 'tr' ? 'Ayet' : 'Ayah'} {lastAyah}
                  </div>
                  <div className="text-sm text-stone-500">{lastSurahInfo.translation_en}</div>
                </div>
                <button
                  type="button"
                  onClick={() => navigate(`/oku/${lastSurah}`)}
                  data-testid="hatim-continue"
                  className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-white shadow-md ${isKidsMode ? 'bg-amber-500 hover:bg-amber-600' : 'bg-teal-700 hover:bg-teal-800'}`}
                >
                  {lang === 'tr' ? 'Devam Et' : 'Continue'} <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </section>

        {/* Surah grid */}
        <section>
          <h2 className="text-xl font-bold text-stone-800 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {lang === 'tr' ? 'Sureler' : 'Surahs'}
          </h2>
          <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 lg:grid-cols-10 gap-2">
            {surahs.map((s) => {
              const done = completed.includes(s.number);
              return (
                <div key={s.number} className="relative">
                  <button
                    type="button"
                    onClick={() => navigate(`/oku/${s.number}`)}
                    data-testid={`hatim-surah-${s.number}`}
                    className={`w-full aspect-square rounded-xl border flex flex-col items-center justify-center p-2 transition-all ${
                      done
                        ? isKidsMode ? 'bg-amber-500 text-white border-amber-600' : 'bg-teal-700 text-white border-teal-800'
                        : 'bg-white text-stone-700 border-stone-200 hover:border-stone-400'
                    }`}
                  >
                    <div className="text-xs opacity-70">{s.number}</div>
                    <div className="text-xs font-semibold truncate w-full text-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {s.name_en.slice(0, 8)}
                    </div>
                  </button>
                  {!done && (
                    <button
                      type="button"
                      onClick={() => completeSurah(s.number)}
                      data-testid={`hatim-complete-${s.number}`}
                      className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-white border border-stone-300 hover:border-green-500 flex items-center justify-center"
                      title={lang === 'tr' ? 'Bitti olarak işaretle' : 'Mark complete'}
                    >
                      <CheckCircle2 className="w-3 h-3 text-stone-400 hover:text-green-600" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </AppShell>
  );
};

export default Hatim;
